import type { BlobStorageKey, SpeechifyURI as SDKSpeechifyURI } from '@speechifyinc/multiplatform-sdk';

import { ExtendedBlobStorageAdapter } from 'lib/speechify/adaptors/blob';
import { useAuthStore } from 'modules/auth/store/authStore';
import { MeasurementKey } from 'modules/profiling/measurementTypes';
import { profilingStoreActions } from 'modules/profiling/profilingStore';
import { getRequiredStringEnv } from 'utils/safeEnvParsing';

import type { MultiplatformSDKInstance } from '../sdk';
import { SDKFacade } from './_base';
import type { ListenableContent } from './listenableContent';
import { ItemListenableContent } from './listenableContent/item';

class IndexedDBItemUsageDataHelper {
  private static readonly DB_NAME = 'speechify-item-usage-data';
  private static readonly STORE_NAME = 'usage-data';
  private db: IDBDatabase | null = null;

  // Keep database connection open since we're the only users of this database
  private async getDatabase(): Promise<IDBDatabase> {
    if (this.db) return this.db;

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(IndexedDBItemUsageDataHelper.DB_NAME);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(IndexedDBItemUsageDataHelper.STORE_NAME)) {
          db.createObjectStore(IndexedDBItemUsageDataHelper.STORE_NAME);
        }
      };
    });
  }

  public markItemAsRecentlyUsed = async (uri: SDKSpeechifyURI): Promise<void> => {
    try {
      const db = await this.getDatabase();
      const transaction = db.transaction(IndexedDBItemUsageDataHelper.STORE_NAME, 'readwrite');
      const store = transaction.objectStore(IndexedDBItemUsageDataHelper.STORE_NAME);

      return new Promise((resolve, reject) => {
        const usageData = {
          itemId: uri.id,
          lastUsedTimestamp: Date.now()
        };

        const request = store.put(usageData, uri.id);

        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error marking item as recently used:', error);
    }
  };

  public getAllItemsUsageData = async (): Promise<Array<{ itemId: string; lastUsedTimestamp: number }>> => {
    try {
      const db = await this.getDatabase();
      const transaction = db.transaction(IndexedDBItemUsageDataHelper.STORE_NAME, 'readonly');
      const store = transaction.objectStore(IndexedDBItemUsageDataHelper.STORE_NAME);

      return new Promise((resolve, reject) => {
        const request = store.getAll();

        request.onsuccess = () => {
          resolve(request.result || []);
        };

        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error getting items usage data:', error);
      return [];
    }
  };
}

export class SDKOfflineAvailabilityFacade extends SDKFacade {
  private static _singleton: SDKOfflineAvailabilityFacade;

  // LRU cleanup thresholds
  private static readonly CLEANUP_THRESHOLD_PERCENT = 60; // Start cleanup when storage exceeds this percentage
  private static readonly TARGET_UTILIZATION_PERCENT = 50; // Continue cleanup until storage is below this percentage

  private _indexedDBItemUsageDataHelper = new IndexedDBItemUsageDataHelper();

  constructor(sdk: MultiplatformSDKInstance) {
    super(sdk);
    SDKOfflineAvailabilityFacade._singleton = this;
  }

  static override get singleton(): SDKOfflineAvailabilityFacade {
    if (!SDKOfflineAvailabilityFacade._singleton) {
      throw new Error('SDKOfflineAvailabilityFacade is not initialized');
    }
    return SDKOfflineAvailabilityFacade._singleton;
  }

  private _removeLibraryItemFromOffline = this.sdk.promisify(this.sdk.client.offlineAvailabilityManager.removeLibraryItemFromOffline);

  // Moved from outside the class and reduced limit to 1GB
  private async checkForStorageUtilizationInPercentRelativeToOneGigaByte(): Promise<number> {
    return profilingStoreActions.measureAction(
      async () => {
        try {
          // Get blob storage adapter
          const blobStorage = this.sdk.platform.getBlobStorage() as ExtendedBlobStorageAdapter;

          // Get total size of all blobs
          const totalSizeInBytes = await blobStorage.getTotalSizeAsync();

          // Calculate percentage relative to 1GB
          const oneGigabyteInBytes = 1 * 1024 * 1024 * 1024; // 1GB in bytes
          const utilizationPercent = Math.min(Math.floor((totalSizeInBytes / oneGigabyteInBytes) * 100), 100);

          return utilizationPercent;
        } catch (error) {
          console.error('[LRU] Error calculating storage utilization:', error);

          // Fallback to Storage API if available
          if (navigator.storage && navigator.storage.estimate) {
            try {
              const estimate = await navigator.storage.estimate();
              const usedBytes = estimate.usage || 0;
              const oneGigabyteInBytes = 1 * 1024 * 1024 * 1024; // 1GB in bytes
              const fallbackPercent = Math.min(Math.floor((usedBytes / oneGigabyteInBytes) * 100), 100);

              return fallbackPercent;
            } catch (fallbackError) {
              console.error('[LRU] Error using fallback storage estimation:', fallbackError);
            }
          }

          // If all else fails, return a conservative estimate
          return 50; // Assume 50% usage to trigger some cleanup but not be too aggressive
        }
      },
      {
        measurementKey: MeasurementKey.sdkStorageUtilizationCheck,
        context: {},
        identifier: {}
      }
    );
  }

  private _maybePerformLRUCleanUpToMakeSpaceFor = async (uri: SDKSpeechifyURI) => {
    const storageUtilizationPercent = await this.checkForStorageUtilizationInPercentRelativeToOneGigaByte();

    if (storageUtilizationPercent < SDKOfflineAvailabilityFacade.CLEANUP_THRESHOLD_PERCENT) {
      return;
    }

    // Get current item's blob storage key pattern to avoid deleting it
    const currentItemKey = await this._getBlobStorageKeyForItemId(uri.id);

    // Perform the actual cleanup
    await this._performLRUCleanup(currentItemKey);
  };

  /**
   * Perform LRU cleanup without being tied to a specific item.
   * This can be called periodically or when the app starts to ensure storage doesn't get too full.
   */
  public performLRUCleanupIfNeeded = async (): Promise<void> => {
    const storageUtilizationPercent = await this.checkForStorageUtilizationInPercentRelativeToOneGigaByte();

    if (storageUtilizationPercent < SDKOfflineAvailabilityFacade.CLEANUP_THRESHOLD_PERCENT) {
      return;
    }

    await this._performLRUCleanup(null);
  };

  /**
   * Core LRU cleanup implementation that can be reused by both cleanup methods
   * @param itemKeyToProtect - Optional key that should not be deleted (e.g., for the item being currently added)
   */
  private _performLRUCleanup = async (
    itemKeyToProtect: string | null
  ): Promise<
    | {
        itemsRemoved: number;
        removedWithoutUsageData: number;
        removedWithUsageData: number;
        unknownPatternsRemoved: number;
      }
    | undefined
  > => {
    try {
      // Get direct access to blob storage
      const blobStorage = this.sdk.platform.getBlobStorage() as ExtendedBlobStorageAdapter;

      // 1. Get all blob storage keys
      const allStorageKeys = await blobStorage.getAllKeysAsync();

      if (allStorageKeys.length === 0) return;

      // 3. Get all items usage data
      const usageDataItems = await this._indexedDBItemUsageDataHelper.getAllItemsUsageData();

      // Create a map of item IDs to last used timestamps for quick lookup
      const usageDataMap = new Map<string, number>();
      for (const item of usageDataItems) {
        usageDataMap.set(item.itemId, item.lastUsedTimestamp);
      }

      // 4. Categorize blob keys into "unknown pattern", "known but unused", and "known and used"
      const itemIdRegex = /\/multiplatform\/import\/.*?\/(.+)$/;
      const unknownPatternKeys: BlobStorageKey[] = [];
      const knownKeysWithoutUsageData: Array<{ key: BlobStorageKey; itemId: string }> = [];
      const knownKeysWithUsageData: Array<{ key: BlobStorageKey; itemId: string; lastUsedTimestamp: number }> = [];

      // Process each blob storage key
      for (const key of allStorageKeys) {
        // Skip protected item's key if provided
        if (itemKeyToProtect && key.originalKey === itemKeyToProtect) continue;

        // Try to extract item ID from the key
        const match = key.originalKey.match(itemIdRegex);
        if (!match) {
          // Key doesn't match our expected pattern, prioritize deleting it
          unknownPatternKeys.push(key);
          continue;
        }

        const itemId = match[1];
        const timestamp = usageDataMap.get(itemId);

        if (timestamp === undefined) {
          // Known pattern but no usage data (never been opened)
          knownKeysWithoutUsageData.push({ key, itemId });
        } else {
          // Known pattern with usage data
          knownKeysWithUsageData.push({ key, itemId, lastUsedTimestamp: timestamp });
        }
      }

      // Sort items with usage data by last used timestamp (oldest first)
      const sortedKeysWithUsageData = knownKeysWithUsageData.sort((a, b) => a.lastUsedTimestamp - b.lastUsedTimestamp);

      // 5. Start deleting blobs in priority order until we reach target utilization
      let itemsRemoved = 0;
      let unknownPatternsRemoved = 0;
      let removedWithoutUsageData = 0;
      let removedWithUsageData = 0;

      // 5a. First remove unknown pattern keys (highest priority)
      for (const key of unknownPatternKeys) {
        // Stop if we've already freed up enough space
        const currentUtilizationPercent = await this.checkForStorageUtilizationInPercentRelativeToOneGigaByte();
        if (currentUtilizationPercent < SDKOfflineAvailabilityFacade.TARGET_UTILIZATION_PERCENT) {
          break;
        }

        // Delete the blob directly from storage
        try {
          const exists = await blobStorage.hasKey(key);
          if (exists) {
            const success = await blobStorage.deleteBlobAsync(key);
            if (success) {
              itemsRemoved++;
              unknownPatternsRemoved++;
            } else {
              console.error(`[LRU] Failed to remove key: ${key.originalKey}`);
            }
          } else {
            console.warn(`[LRU] Blob not found for key: ${key.originalKey}`);
          }
        } catch (error) {
          console.error(`[LRU] Failed to remove key ${key.originalKey}:`, error);
        }
      }

      // 5b. Next remove items without usage data
      for (const { key } of knownKeysWithoutUsageData) {
        // Stop if we've already freed up enough space
        const currentUtilizationPercent = await this.checkForStorageUtilizationInPercentRelativeToOneGigaByte();
        if (currentUtilizationPercent < SDKOfflineAvailabilityFacade.TARGET_UTILIZATION_PERCENT) {
          break;
        }

        // Delete the blob directly from storage
        try {
          const exists = await blobStorage.hasKey(key);
          if (exists) {
            const success = await blobStorage.deleteBlobAsync(key);
            if (success) {
              itemsRemoved++;
              removedWithoutUsageData++;
            } else {
              console.error(`[LRU] Failed to remove key: ${key.originalKey}`);
            }
          } else {
            console.warn(`[LRU] Blob not found for key: ${key.originalKey}`);
          }
        } catch (error) {
          console.error(`[LRU] Failed to remove key ${key.originalKey}:`, error);
        }
      }

      // 5c. If we still need to free up space, remove older used items
      if ((await this.checkForStorageUtilizationInPercentRelativeToOneGigaByte()) >= SDKOfflineAvailabilityFacade.TARGET_UTILIZATION_PERCENT) {
        for (const { key } of sortedKeysWithUsageData) {
          // Stop if we've already freed up enough space
          const currentUtilizationPercent = await this.checkForStorageUtilizationInPercentRelativeToOneGigaByte();
          if (currentUtilizationPercent < SDKOfflineAvailabilityFacade.TARGET_UTILIZATION_PERCENT) {
            break;
          }

          // Delete the blob directly from storage
          try {
            const exists = await blobStorage.hasKey(key);
            if (exists) {
              const success = await blobStorage.deleteBlobAsync(key);
              if (success) {
                itemsRemoved++;
                removedWithUsageData++;
              } else {
                console.error(`[LRU] Failed to remove key: ${key.originalKey}`);
              }
            } else {
              console.warn(`[LRU] Blob not found for key: ${key.originalKey}`);
            }
          } catch (error) {
            console.error(`[LRU] Failed to remove key ${key.originalKey}:`, error);
          }
        }
      }

      return {
        itemsRemoved,
        removedWithoutUsageData,
        removedWithUsageData,
        unknownPatternsRemoved
      };
    } catch (error) {
      console.error('[LRU] Error performing LRU cleanup:', error);
    }
  };

  private _makeAvailableOffline = async (uri: SDKSpeechifyURI) => {
    const { OfflineAvailabilityStatus } = this.sdk.sdkModule;
    const handleFinish = () => {
      this._maybePerformLRUCleanUpToMakeSpaceFor(uri);
      this._indexedDBItemUsageDataHelper.markItemAsRecentlyUsed(uri);
    };

    this.sdk.client.offlineAvailabilityManager.makeLibraryItemAvailableOffline(uri, null);

    this.sdk.client.offlineAvailabilityManager.getOfflineAvailabilityStatusFlow(uri, result => {
      result.ifSuccessful(offlineAvailabilityStatusKotlinFlow => {
        if (offlineAvailabilityStatusKotlinFlow.lastValue && offlineAvailabilityStatusKotlinFlow.lastValue === OfflineAvailabilityStatus.AVAILABLE) {
          handleFinish();
          return;
        }

        const unsubscribe = offlineAvailabilityStatusKotlinFlow.subscribe(status => {
          if (status === OfflineAvailabilityStatus.DOWNLOADING) {
            // no-op, let's wait until the status transitions to AVAILABLE (finished downloading)
            return;
          }
          if (status === OfflineAvailabilityStatus.AVAILABLE) {
            handleFinish();
            unsubscribe.destroy();
          }
        });
      });
    });
  };

  public makeURIAvailableOffline = (uri: SDKSpeechifyURI) => {
    this._makeAvailableOffline(uri);
  };

  public makeContentAvailableOffline = (listenableContent: ListenableContent) => {
    if (listenableContent instanceof ItemListenableContent) {
      this._makeAvailableOffline(listenableContent.sdkItem.uri);
    }
    // for other type of ListenableContent, the `makeURIAvailableOffline` will be called once the uri is ready
    // see: `handleImportResult` in `src/components/library/import/upload/import.ts`
  };

  public markItemAsRecentlyUsed = (uri: SDKSpeechifyURI) => {
    this._indexedDBItemUsageDataHelper.markItemAsRecentlyUsed(uri);
  };

  private _getBlobStorageKeyForItemId = async (itemId: string) => {
    const projectId = getRequiredStringEnv('NEXT_PUBLIC_FB_PROJECT_ID');
    const userId = useAuthStore.getState().user!.uid || '';
    return `gs://${projectId}.appspot.com/multiplatform/import/${userId}/${itemId}`;
  };

  public isItemAvailableOffline = async (uri: SDKSpeechifyURI) => {
    const blobStorageKey = await this._getBlobStorageKeyForItemId(uri.id);
    const blobStorage = this.sdk.platform.getBlobStorage() as ExtendedBlobStorageAdapter;
    // Use the new blobExists method which properly checks if content exists
    const isAvailable = await profilingStoreActions.measureAction(() => blobStorage.blobExists(blobStorageKey), {
      measurementKey: MeasurementKey.sdkIsItemAvailableOffline,
      context: {},
      identifier: {
        itemId: uri.id
      }
    });
    return isAvailable;
  };
}
